/**
 * PeggingTrainer - Interactive Cribbage Pegging Tactics Trainer
 * Generates and evaluates tactical pegging scenarios to train players on:
 * - Baiting 15s vs sequence defense
 * - Safe maneuvering toward 31
 * - Pair vs Pair Royal trap defense
 * - Go traps and low-card sweeps
 * - Endgame pegging to hole 121
 */

import { Card } from '../models/Card.js';
import { Deck } from '../models/Deck.js';
import { CribbageScorer } from '../scoring/CribbageScorer.js';
import { VAL } from '../scoring/ScoringConstants.js';
import { AchievementManager } from '../achievements/AchievementManager.js';
import { Profile } from '../profile/Profile.js';

export const PEGGING_CATEGORIES = [
  { id: 'all', name: 'All Scenarios (Mixed Challenges)' },
  { id: 'baiting_15s', name: 'Baiting & Defending the 15' },
  { id: 'sequence_defense', name: 'Run Defense & Sequence Traps' },
  { id: 'pair_dilemmas', name: 'Pairing vs. Pair Royal Danger' },
  { id: 'maneuver_31', name: 'The 31-Point Maneuver' },
  { id: 'go_traps', name: 'Go Traps & Low Card Sweeps' },
  { id: 'endgame', name: 'Endgame Pegging (Race to 121)' }
];

export class PeggingTrainer {
  /**
   * In-session statistics.
   */
  static stats = {
    scenariosPlayed: 0,
    optimalCount: 0,
    currentStreak: 0,
    bestStreak: 0,
    totalEVDiff: 0
  };

  /**
   * Resets session statistics.
   */
  static resetStats(options = {}) {
    PeggingTrainer.stats = {
      scenariosPlayed: 0,
      optimalCount: 0,
      currentStreak: 0,
      bestStreak: 0,
      totalEVDiff: 0
    };
    if (options && options.career) {
      try {
        const prof = Profile.getInstance(options.storage);
        if (prof && typeof prof.clearPeggingTrainerStats === 'function') {
          prof.clearPeggingTrainerStats(options.storage);
        }
      } catch (_) {}
    }
  }

  /**
   * Retrieves session and career stats.
   * @param {Object} [storage=null]
   */
  static getStats(storage = null) {
    const accuracyPct = PeggingTrainer.stats.scenariosPlayed > 0
      ? Math.round((PeggingTrainer.stats.optimalCount / PeggingTrainer.stats.scenariosPlayed) * 100)
      : 0;

    let career = null;
    try {
      const prof = Profile.getInstance(storage);
      if (prof && typeof prof.getPeggingTrainerStats === 'function') {
        career = prof.getPeggingTrainerStats(storage);
      }
    } catch (_) {}

    return {
      session: {
        ...PeggingTrainer.stats,
        accuracyPct
      },
      career: career || {
        scenariosPlayed: 0,
        optimalCount: 0,
        accuracyPct: 0,
        currentStreak: 0,
        bestStreak: 0
      }
    };
  }

  /**
   * Curated scenario definitions illustrating foundational pegging tenets.
   */
  static CURATED_SCENARIOS = [
    {
      id: 'bait_5_lead',
      category: 'baiting_15s',
      title: 'Opening Lead Dilemma — Never Lead a 5',
      description: 'You are Pone and have the opening lead. You hold a 4, a 5, a 10, and a King. Which card do you lead?',
      pile: [],
      count: 0,
      hand: [
        new Card({ r: 4, s: 'D', id: '4D' }),
        new Card({ r: 5, s: 'S', id: '5S' }),
        new Card({ r: 10, s: 'C', id: '10C' }),
        new Card({ r: 13, s: 'H', id: 'KH' })
      ],
      lesson: 'Never lead a 5! Since over 30% of the entire deck consists of 10-value cards (10, J, Q, K), leading a 5 virtually guarantees your opponent scores 15-for-2 on their first response. Leading a 4 is the safest disciplined play, keeping the count under 15.'
    },
    {
      id: 'bait_10_lead_hold_pair',
      category: 'baiting_15s',
      title: 'Opponent Leads a 10 — Armed with Hidden Fives',
      description: 'Your opponent leads a King (10-value). You hold two 5s in your hand. How do you play?',
      pile: [new Card({ r: 13, s: 'S', id: 'KS' })],
      count: 10,
      hand: [
        new Card({ r: 5, s: 'D', id: '5D' }),
        new Card({ r: 5, s: 'C', id: '5C' }),
        new Card({ r: 8, s: 'H', id: '8H' }),
        new Card({ r: 11, s: 'S', id: 'JS' })
      ],
      lesson: 'Playing a 5 scores 15-for-2 (+2 pts) with complete security. Because you hold the backup 5 in hand, if your opponent pairs your 5 for 2 pts, you can immediately counter with Pair Royal for 6 pts (count 25)!'
    },
    {
      id: 'seq_def_4_lead',
      category: 'sequence_defense',
      title: 'Opponent Leads a 4 — The 4-5-6 Ambush Trap',
      description: 'Opponent leads a 4 (Count: 4). You hold a 5, an 8, and a King. Do you play the 5, or play disciplined defense?',
      pile: [new Card({ r: 4, s: 'C', id: '4C' })],
      count: 4,
      hand: [
        new Card({ r: 5, s: 'D', id: '5D' }),
        new Card({ r: 8, s: 'S', id: '8S' }),
        new Card({ r: 13, s: 'H', id: 'KH' })
      ],
      lesson: 'Never play a 5 on a 4 lead! Playing a 5 reaches count 9, inviting the opponent to drop a 6 for 15-for-2 PLUS a 3-card run (4-5-6 for 3 pts) = 5 devastating points! Playing an 8 (count 12) or King (count 14) safely defuses the trap.'
    },
    {
      id: 'seq_counter_run',
      category: 'sequence_defense',
      title: 'Opponent Triggers a 3-Card Run — The Coup de Grâce',
      description: 'Pile sequence is 6 - 7 - 8 (Count: 21). Opponent just scored 3 pts for the run. Can you counter?',
      pile: [
        new Card({ r: 6, s: 'H', id: '6H' }),
        new Card({ r: 7, s: 'S', id: '7S' }),
        new Card({ r: 8, s: 'D', id: '8D' })
      ],
      count: 21,
      hand: [
        new Card({ r: 9, s: 'C', id: '9C' }),
        new Card({ r: 2, s: 'D', id: '2D' }),
        new Card({ r: 10, s: 'S', id: '10S' })
      ],
      lesson: 'Playing the 9 reaches count 30 and extends the sequence to a 4-card run (6-7-8-9) for 4 points! It also puts the count at 30, virtually assuring you a 1-point Go on the next card for 5 total points.'
    },
    {
      id: 'pair_danger_4',
      category: 'pair_dilemmas',
      title: 'Opponent Leads a 4 — The Pair Royal Ambush',
      description: 'Opponent leads a 4. You hold a 4, a 7, an 8, and a Queen (no backup 4). Do you pair the 4?',
      pile: [new Card({ r: 4, s: 'S', id: '4S' })],
      count: 4,
      hand: [
        new Card({ r: 4, s: 'C', id: '4C' }),
        new Card({ r: 7, s: 'H', id: '7H' }),
        new Card({ r: 8, s: 'D', id: '8D' }),
        new Card({ r: 12, s: 'S', id: 'QS' })
      ],
      lesson: 'Pairing low cards like 3s, 4s, and 5s early in the hand without a backup in hand carries severe risk. If the opponent holds a third 4, they counter-strike with Pair Royal for 6 points at count 12. Playing Queen (count 14) is disciplined defense.'
    },
    {
      id: 'maneuver_31_exact',
      category: 'maneuver_31',
      title: 'Count is 21 — Seizing the Thirty-One',
      description: 'Count is at 21 after intense pegging. You have an Ace, a 5, and a 10. How do you maneuver?',
      pile: [
        new Card({ r: 10, s: 'D', id: '10D' }),
        new Card({ r: 8, s: 'H', id: '8H' }),
        new Card({ r: 3, s: 'C', id: '3C' })
      ],
      count: 21,
      hand: [
        new Card({ r: 1, s: 'S', id: 'AS' }),
        new Card({ r: 5, s: 'S', id: '5S' }),
        new Card({ r: 10, s: 'C', id: '10C' })
      ],
      lesson: 'Playing the 10 hits exactly 31 for 2 points! Playing the 5 advances the count to 26, which gives the opponent an easy 31 if they hold a 5. Playing the Ace hits 22, yielding initiative.'
    },
    {
      id: 'go_trap_sweep',
      category: 'go_traps',
      isGo: true,
      title: 'Opponent Plays 2 to Count 28 and Calls GO — The Double-Dip',
      description: 'Opponent plays a 2 to reach count 28 and calls GO! You hold a 2, an Ace, and an 8. In what order do you play them?',
      pile: [
        new Card({ r: 10, s: 'S', id: '10S' }),
        new Card({ r: 9, s: 'D', id: '9D' }),
        new Card({ r: 7, s: 'H', id: '7H' }),
        new Card({ r: 2, s: 'S', id: '2S' })
      ],
      count: 28,
      hand: [
        new Card({ r: 2, s: 'C', id: '2C' }),
        new Card({ r: 1, s: 'H', id: 'AH' }),
        new Card({ r: 8, s: 'D', id: '8D' })
      ],
      lesson: 'Order of play on a GO is crucial! By playing your 2 first (count 30), you pair the opponent\'s 2 for 2 points, and then immediately follow up with your Ace to hit 31 for 2 more points, capturing 4 total points! If you played the Ace first to count 29, it separates the 2s and destroys your pair, yielding only 2 points for 31.'
    },
    {
      id: 'go_trap_snag_31',
      category: 'go_traps',
      isGo: true,
      title: 'Opponent Calls Go at 28 — Snagging 31 vs Stranded Go',
      description: 'Opponent is stuck at count 28 and calls GO! You hold an Ace, a 3, and a 9. Which card do you play?',
      pile: [
        new Card({ r: 10, s: 'S', id: '10S' }),
        new Card({ r: 9, s: 'D', id: '9D' }),
        new Card({ r: 9, s: 'H', id: '9H' })
      ],
      count: 28,
      hand: [
        new Card({ r: 3, s: 'C', id: '3C' }),
        new Card({ r: 1, s: 'H', id: 'AH' }),
        new Card({ r: 9, s: 'S', id: '9S' })
      ],
      lesson: 'When an opponent calls GO, always check if you can hit exactly 31! Playing the 3 seizes 31 for 2 points. If you play the Ace first to count 29, your 3 is immediately blocked (29 + 3 = 32 exceeds 31), forcing a 1-point Go and leaving thirty-one on the table.'
    },
    {
      id: 'endgame_119_pegout',
      category: 'endgame',
      title: 'Endgame: Score 119 vs 120 — Instant Peg-Out',
      description: 'You are at hole 119 (2 pts needed to win). Opponent is at 120. Opponent leads a 3. What is your move?',
      pile: [new Card({ r: 3, s: 'H', id: '3H' })],
      count: 3,
      hand: [
        new Card({ r: 3, s: 'D', id: '3D' }),
        new Card({ r: 6, s: 'S', id: '6S' }),
        new Card({ r: 9, s: 'C', id: '9C' }),
        new Card({ r: 13, s: 'D', id: 'KD' })
      ],
      lesson: 'In the endgame when within pegging distance of 121, pairing for 2 points wins the entire match instantly! Pair Royal risk does not matter because the game terminates the millisecond your 121st hole is reached.'
    }
  ];

  /**
   * Generates a scenario by category or procedural random deal.
   * @param {string} [categoryId='all']
   * @returns {Object}
   */
  static generateScenario(categoryId = 'all') {
    let pool = PeggingTrainer.CURATED_SCENARIOS;
    if (categoryId && categoryId !== 'all') {
      const filtered = pool.filter(s => s.category === categoryId);
      if (filtered.length) pool = filtered;
    }

    const template = pool[Math.floor(Math.random() * pool.length)];

    // Deep clone scenario objects to allow clean interactive gameplay
    const scenario = {
      id: `${template.id}_${Date.now()}`,
      templateId: template.id,
      category: template.category,
      title: template.title,
      description: template.description,
      lesson: template.lesson,
      isGo: Boolean(template.isGo || template.category === 'go_traps'),
      pile: template.pile.map(c => new Card({ r: c.rank, s: c.suit, id: c.id })),
      count: template.count,
      hand: template.hand.map(c => new Card({ r: c.rank, s: c.suit, id: c.id }))
    };

    scenario.analysis = PeggingTrainer.analyzeScenario(scenario);
    return scenario;
  }

  /**
   * Evaluates all legal moves for the player in the scenario.
   * @param {Object} scenario
   * @returns {Object}
   */
  static analyzeScenario(scenario) {
    const { pile, count, hand } = scenario;
    const isGo = Boolean(scenario.isGo || scenario.category === 'go_traps');
    const legalCards = hand.filter(c => count + VAL(c.rank) <= 31);

    const evaluatedMoves = legalCards.map(card => {
      const v = VAL(card.rank);
      const newCount = count + v;
      const testPile = [...pile, card];

      // Direct points scored on this play
      const rawPts = CribbageScorer.scorePegging(testPile, scenario.options || {});
      const directPts = rawPts.reduce((s, p) => s + p.n, 0);

      // Defense & Counter-risk analysis:
      let counterRisk = 0;
      let reasons = [];

      if (directPts > 0) {
        reasons.push(`Scores +${directPts} pts (${rawPts.map(p => p.why).join(', ') || '31'})`);
      }

      if (!isGo) {
        // 1. Walking into a 15 (Opponent can play a 10 to hit 15, or count equals 5)
        if (newCount === 5) {
          counterRisk += 2.0;
          reasons.push('⚠️ Leaves count at 5; opponent holding any 10-value card scores 15-for-2');
        } else if (newCount > 5 && newCount < 15 && (15 - newCount) <= 10) {
          const gap = 15 - newCount;
          if (gap === 5) {
            counterRisk += 1.6;
            reasons.push(`Leaves count at ${newCount}; vulnerable to an opponent 5 (15-for-2)`);
          } else if (gap >= 1 && gap <= 4) {
            counterRisk += 1.1;
          }
        }

        // 2. Walking into 31
        if (newCount === 21) {
          counterRisk += 2.0;
          reasons.push('⚠️ Leaves count at 21; any 10-value card from opponent hits 31-for-2');
        } else if (newCount >= 22 && newCount <= 30) {
          const gap = 31 - newCount;
          if (gap <= 5) {
            counterRisk += 1.4;
            reasons.push(`Leaves count at ${newCount}; vulnerable to opponent 31-for-2`);
          }
        }

        // 3. Pairing low cards danger (Pair Royal exposure)
        if (pile.length > 0) {
          const oppLast = pile[pile.length - 1];
          if (oppLast.rank === card.rank && VAL(card.rank) <= 5 && pile.length === 1) {
            const hasBackup = hand.filter(c => c.rank === card.rank).length >= 2;
            if (!hasBackup) {
              counterRisk += 3.2;
              reasons.push(`⚠️ Pairing a ${card.rankName || card.rank} without a backup in hand risks opponent counter-striking with Pair Royal for 6 pts`);
            }
          }
        }

        // 4. 4-5-6 trap detection (opponent can hit 15 AND 3-card run for 5 pts)
        if (testPile.length === 2) {
          const r1 = testPile[0].rank;
          const r2 = card.rank;
          const ranks = [r1, r2].sort((a, b) => a - b);
          if ((ranks[0] === 4 && ranks[1] === 5) || (ranks[0] === 5 && ranks[1] === 6)) {
            counterRisk += 3.2;
            const missing = ranks[0] === 4 ? 6 : 4;
            reasons.push(`⚠️ Walking into the 4-5-6 trap: opponent ${missing} scores 15-for-2 AND a 3-card run for 5 points!`);
          }
        }

        // 5. Run defense: check if this play leaves a dangerous consecutive open sequence
        if (testPile.length >= 2) {
          const r1 = testPile[testPile.length - 2].rank;
          const r2 = card.rank;
          if (Math.abs(r1 - r2) === 1 && newCount <= 22) {
            counterRisk += 1.2;
            reasons.push('Leaves consecutive run open for opponent');
          }
        }
      }

      // Positive strategic bonuses
      let stratBonus = 0;
      if (newCount === 31) stratBonus += 3.5;
      if (newCount === 15) stratBonus += 2.5;
      if (directPts >= 3) stratBonus += directPts * 1.2;

      // Endgame peg-out priority: any points that win the match immediately override all counter-risks
      const isEndgame = scenario.category === 'endgame' || scenario.isEndgame;
      if (isEndgame && directPts >= 2) {
        stratBonus += 10.0;
        counterRisk = 0;
        reasons = reasons.filter(r => !r.startsWith('⚠️') && !r.includes('Leaves count'));
        reasons.unshift('🏆 Pegs out! Captures match-winning points instantly (game ends immediately)');
      }

      // When opponent is at Go, check follow-up potential with remaining hand
      if (isGo && newCount < 31) {
        const remaining = hand.filter(h => h.id !== card.id);
        const followUps = remaining.filter(h => newCount + VAL(h.rank) <= 31);
        if (followUps.length > 0) {
          const hits31 = followUps.some(h => newCount + VAL(h.rank) === 31);
          if (hits31) {
            stratBonus += 2.5;
            reasons.push('Keeps count open to sweep 31 (+2 pts) with remaining card in hand');
          } else {
            stratBonus += 1.0;
            reasons.push('Allows playing an additional card under 31');
          }
        }
      }

      // Net tactical rating
      const netScore = Number((directPts + stratBonus - counterRisk).toFixed(2));

      return {
        card,
        id: card.id,
        newCount,
        directPts,
        counterRisk: Number(counterRisk.toFixed(2)),
        netScore,
        reasons
      };
    });

    // Sort moves from highest tactical netScore to lowest
    evaluatedMoves.sort((a, b) => b.netScore - a.netScore);

    // Assign ranks and EV diff from optimal
    const optimal = evaluatedMoves[0] || null;
    evaluatedMoves.forEach((m, idx) => {
      m.rank = idx + 1;
      m.diff = optimal ? Number((optimal.netScore - m.netScore).toFixed(2)) : 0;
      if (m.diff === 0) {
        m.grade = 'optimal';
        m.gradeLabel = 'Optimal Choice! 🏆';
      } else if (m.diff <= 1.2) {
        m.grade = 'great';
        m.gradeLabel = 'Safe Alternative 👍';
      } else if (m.diff <= 2.8) {
        m.grade = 'risky';
        m.gradeLabel = 'Risky Play ⚠️';
      } else {
        m.grade = 'blunder';
        m.gradeLabel = 'Blunder / Trapped ❌';
      }
    });

    return {
      legalMoves: evaluatedMoves,
      optimal,
      hasLegalMoves: evaluatedMoves.length > 0
    };
  }

  /**
   * Evaluates the player's selected card in the scenario and records stats.
   * @param {Object} scenario
   * @param {Card} chosenCard
   * @param {Object} [options]
   * @returns {Object}
   */
  static evaluateChoice(scenario, chosenCard, options = {}) {
    if (!scenario || !scenario.analysis || !chosenCard) return null;

    const match = scenario.analysis.legalMoves.find(m => m.card.id === chosenCard.id);
    if (!match) return null;

    const isOptimal = match.rank === 1;
    const diff = match.diff;

    // Record session statistics
    PeggingTrainer.stats.scenariosPlayed++;
    if (isOptimal) {
      PeggingTrainer.stats.optimalCount++;
      PeggingTrainer.stats.currentStreak++;
      if (PeggingTrainer.stats.currentStreak > PeggingTrainer.stats.bestStreak) {
        PeggingTrainer.stats.bestStreak = PeggingTrainer.stats.currentStreak;
      }
      const sfx = (typeof window !== 'undefined' ? window.sfx : null);
      AchievementManager.incrementStat('peggingTrainerOptimalPlays', 1, { sfx });
    } else {
      PeggingTrainer.stats.currentStreak = 0;
    }
    PeggingTrainer.stats.totalEVDiff += diff;

    // Record in global Profile across all brands
    let career = null;
    try {
      const prof = Profile.getInstance(options.storage);
      if (prof && typeof prof.recordPeggingTrainerPlay === 'function') {
        career = prof.recordPeggingTrainerPlay({ isOptimal, diff }, options.storage);
      }
    } catch (_) {}

    return {
      scenario,
      chosen: match,
      optimal: scenario.analysis.optimal,
      isOptimal,
      diff,
      grade: match.grade,
      gradeLabel: match.gradeLabel,
      lesson: scenario.lesson,
      stats: {
        ...PeggingTrainer.stats,
        accuracyPct: Math.round((PeggingTrainer.stats.optimalCount / PeggingTrainer.stats.scenariosPlayed) * 100),
        career
      }
    };
  }
}
